import type { GooglePlaceDto } from '@up2stay/up2promo-api-client';

import { LocationAddressEditor } from '@/core/components/locations/location-address-editor';
import {
  type LocationAddressDetails,
  LocationDetailsForm,
} from '@/core/components/locations/location-details-form';
import { ProductsSetupStepShell } from '@/routes/app/products-setup-wizard/products-setup-step-shell';

export type ProductsSetupCreateLocationLabels = {
  title?: string;
  description?: string;
  reviewChannelsHint?: string;
  nameLabel?: string;
  namePlaceholder?: string;
  searchLabel?: string;
  searchPlaceholder?: string;
  searchHint?: string;
  addressLabel?: string;
  selectedEmptyLabel?: string;
  addressEditHint?: string;
  mapPreviewLabel?: string;
  noCoordinatesLabel?: string;
  mapUnavailableLabel?: string;
  dragHintLabel?: string;
};

export type ProductsSetupStepCreateLocationProps = {
  name: string;
  onNameChange: (name: string) => void;
  nameError?: string | null;
  addressDetails: LocationAddressDetails | null;
  onAddressDetailsChange: (details: LocationAddressDetails) => void;
  latitude: number | undefined;
  longitude: number | undefined;
  onCoordinatesChange: (
    latitude: number | undefined,
    longitude: number | undefined,
  ) => void;
  onPlaceSelect: (place: GooglePlaceDto) => void;
  disabled?: boolean;
  labels: ProductsSetupCreateLocationLabels;
};

/**
 * Nested one-step location create inside product setup.
 * Intentionally details-only (no REVIEW step). Structure leaves room for a
 * future sibling nested phase (e.g. link another review channel) without
 * folding multi-provider linking into place search.
 */
export function ProductsSetupStepCreateLocation({
  name,
  onNameChange,
  nameError,
  addressDetails,
  onAddressDetailsChange,
  latitude,
  longitude,
  onCoordinatesChange,
  onPlaceSelect,
  disabled = false,
  labels,
}: ProductsSetupStepCreateLocationProps) {
  return (
    <ProductsSetupStepShell
      title={labels.title ?? ''}
      description={labels.description}
      footnote={labels.reviewChannelsHint}
    >
      <div className="flex flex-col gap-6">
        <LocationDetailsForm
          name={name}
          onNameChange={onNameChange}
          nameError={nameError}
        />
        <LocationAddressEditor
          addressDetails={addressDetails}
          onAddressDetailsChange={onAddressDetailsChange}
          latitude={latitude}
          longitude={longitude}
          onCoordinatesChange={onCoordinatesChange}
          onPlaceSelect={onPlaceSelect}
          name={name}
          disabled={disabled}
          labels={{
            searchLabel: labels.searchLabel,
            searchPlaceholder: labels.searchPlaceholder,
            searchHint: labels.searchHint,
            addressLabel: labels.addressLabel,
            selectedEmptyLabel: labels.selectedEmptyLabel,
            addressEditHint: labels.addressEditHint,
            mapPreviewLabel: labels.mapPreviewLabel,
            noCoordinatesLabel: labels.noCoordinatesLabel,
            mapUnavailableLabel: labels.mapUnavailableLabel,
            dragHintLabel: labels.dragHintLabel,
          }}
        />
      </div>
    </ProductsSetupStepShell>
  );
}
